/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { toTitleCase } from 'sarsaparilla';
import type {
    ContentSuggestion,
    HistorySuggestion,
    InventorySuggestion,
    LocationSuggestion,
    SearchSuggestionResult,
} from 'shared-ui/api/search/search.types';

import type {
    SearchSuggestionGroup,
    SearchSuggestionIcon,
    SearchSuggestionItem,
} from './QuickSearch.types';

type SuggestionPartial = { entity_type: string; campsite_type_of_use?: string[] };

/** Display order of inventory suggestions by entity type. Unlisted types sort last. */
const INVENTORY_TYPE_ORDER: Record<string, number> = {
    recarea: 0,
    campground: 2,
    pass: 1,
    interagencypass: 1,
    activitypass: 1,
    ticketfacility: 3,
    tour: 3,
    timedentry: 3,
    timedentry_tour: 3,
    permit: 4,
    vehiclepermit: 4,
    treepermit: 4,
};

const UNLISTED_INVENTORY_TYPE_ORDER = 5;

function getInventoryTypeOrder({ entity_type }: SuggestionPartial) {
    return INVENTORY_TYPE_ORDER[entity_type] ?? UNLISTED_INVENTORY_TYPE_ORDER;
}

export function getInventoryIcon(suggestion: SuggestionPartial): SearchSuggestionIcon {
    switch (suggestion.entity_type) {
        case 'recarea':
            return 'tree';

        case 'campground': {
            if (
                suggestion?.campsite_type_of_use?.length === 1 &&
                suggestion?.campsite_type_of_use[0] === 'Day'
            ) {
                return 'picnic-table';
            }
            return 'tent';
        }

        case 'ticketfacility':
        case 'tour':
        case 'timedentry':
        case 'timedentry_tour':
            return 'ticket';

        case 'permit':
        case 'vehiclepermit':
        case 'treepermit':
            return 'permit';

        case 'pass':
        case 'interagencypass':
        case 'activitypass':
            return 'card';

        case 'venuereservations':
        case 'venuereservations_venue':
            return 'celebration';
        case 'kb':
            return 'help-outline';
        case 'cms':
        case 'page':
            return 'description';

        default:
            return 'compass';
    }
}

export function transformLocationSuggestions(
    suggestions?: LocationSuggestion[] | null
): SearchSuggestionItem[] {
    if (!suggestions) {
        return [];
    }

    return suggestions.map((suggestion) => {
        return {
            text: suggestion.text,
            icon: 'pin',
            lat: suggestion.lat,
            lng: suggestion.lng,
            lat_ne: suggestion.lat_ne,
            lng_ne: suggestion.lng_ne,
            lat_sw: suggestion.lat_sw,
            lng_sw: suggestion.lng_sw,
            location: suggestion.text,
            id: `${suggestion.lat}-${suggestion.lng}`,
            type: 'location',
            source: suggestion,
        };
    });
}

export function transformInventorySuggestions(
    suggestions?: InventorySuggestion[] | null
): SearchSuggestionItem[] {
    if (!suggestions) {
        return [];
    }

    // The sort is stable, so the API's relevance order holds within each type
    const sorted = [...suggestions].sort(
        (a, b) => getInventoryTypeOrder(a) - getInventoryTypeOrder(b)
    );

    return sorted.map((suggestion) => {
        return {
            text: toTitleCase(suggestion.name),
            description: getInventoryDescription(suggestion),
            icon: getInventoryIcon(suggestion),
            lat: suggestion.lat,
            lng: suggestion.lng,
            location: toTitleCase(suggestion.name),
            // Entity IDs are only unique per type — a rec area and its site pass share one
            id: `${suggestion.entity_type}-${suggestion.entity_id}`,
            orgId: suggestion.org_id,
            entityType: suggestion.entity_type,
            type: 'inventory',
            source: suggestion,
        };
    });
}

function getInventoryDescription({
    parent_name,
    state_code,
    city,
    entity_type,
}: Partial<InventorySuggestion>) {
    if (parent_name) {
        return `${parent_name}${state_code ? `, ${state_code}` : ''}`;
    }

    if (city && state_code) {
        return `Near ${city}, ${state_code}`;
    }

    return entity_type ? formatEntityType(entity_type) : '';
}

/**
 * Search history holds copies of whatever was selected — inventory, places, content —
 * plus bare free-text searches, so each entry is shown the way its original was.
 */
export function transformSearchHistory(
    history?: HistorySuggestion[] | null
): SearchSuggestionItem[] {
    if (!history) {
        return [];
    }

    return history
        .filter((suggestion) => !!suggestion.text)
        .map((suggestion, index) => {
            const { entity_id, entity_type, lat, lng } = suggestion;
            const isEntity = !!(entity_id && entity_type);

            return {
                text: isEntity ? toTitleCase(suggestion.text) : suggestion.text,
                description: isEntity ? getInventoryDescription(suggestion) : undefined,
                icon: getHistoryIcon(suggestion),
                lat,
                lng,
                // History is deduplicated by sarsaparilla, but not by any one field
                id: `recent-${index}`,
                orgId: suggestion.org_id,
                entityType: entity_type,
                type: 'recent',
                source: suggestion,
            };
        });
}

function getHistoryIcon(suggestion: HistorySuggestion): SearchSuggestionIcon {
    if (suggestion.entity_type) {
        return getInventoryIcon({ ...suggestion, entity_type: suggestion.entity_type });
    }

    // A saved place, as opposed to a free-text search
    if (suggestion.lat && suggestion.lng) {
        return 'pin';
    }

    return 'map';
}

export function transformContentSuggestions(
    suggestions?: ContentSuggestion[] | null
): SearchSuggestionItem[] {
    if (!suggestions) {
        return [];
    }

    return suggestions.map((suggestion) => {
        return {
            text: toTitleCase(suggestion.name),
            description: suggestion.parent_name,
            icon: getInventoryIcon(suggestion),
            id: suggestion.entity_id,
            type: 'content',
            source: suggestion,
        };
    });
}

export function transformSearchSuggestionResult(
    result?: SearchSuggestionResult
): SearchSuggestionGroup[] {
    if (!result) {
        return [];
    }

    /**
     * Only include groups that have items. Passing empty groups to Combobox.Root
     * causes base-ui to skip the render prop for them while leaving the previous
     * group's DOM nodes in place, making stale suggestions appear above new ones.
     */
    const groups: SearchSuggestionGroup[] = [
        {
            groupType: 'inventory',
            label: 'Recreation areas, campgrounds, passes, tickets, permits',
            items: transformInventorySuggestions(result.inventory_suggestions),
        },
        {
            groupType: 'locations',
            label: 'Search around these locations',
            items: transformLocationSuggestions(result.suggestions),
        },
        {
            groupType: 'content',
            label: 'Help topics, frequently asked questions, articles',
            items: transformContentSuggestions(result.content_suggestions),
        },
    ];

    return groups.filter((group) => group.items.length > 0);
}

/**
 * Places a free-text search row above suggestion groups.
 * The row tracks the live input; suggestion groups stay on the debounced query.
 * An empty query leaves the groups unchanged so callers can hide the list themselves.
 */
export function prependQuerySuggestion(
    groups: SearchSuggestionGroup[],
    query: string
): SearchSuggestionGroup[] {
    const trimmedQuery = query.trim();

    if (!trimmedQuery) {
        return groups;
    }

    return [
        {
            groupType: 'query',
            items: [
                {
                    id: 'search-query',
                    type: 'query',
                    text: trimmedQuery,
                    icon: 'map',
                },
            ],
        },
        ...groups,
    ];
}

export const NEAR_ME_TEXT = 'Search map for facilities near me ...';
export const CLEAR_HISTORY_TEXT = 'Clear search history';

/** The text a suggestion row shows as its label. */
export function getSuggestionLabel(item: SearchSuggestionItem): string {
    return item.type === 'query' ? `Search map for "${item.text}"` : item.text;
}

/**
 * What the palette offers before anything is typed: a near-me search, then recent
 * searches ending in a row that clears them.
 */
export function createEmptyStateGroups(
    history?: HistorySuggestion[] | null
): SearchSuggestionGroup[] {
    const groups: SearchSuggestionGroup[] = [
        {
            groupType: 'near-me',
            items: [{ id: 'near-me', type: 'near-me', text: NEAR_ME_TEXT, icon: 'map' }],
        },
    ];

    const recentItems = transformSearchHistory(history);

    if (recentItems.length > 0) {
        groups.push({
            groupType: 'recent',
            label: 'Recent searches',
            items: [
                ...recentItems,
                {
                    id: 'clear-history',
                    type: 'clear-history',
                    text: CLEAR_HISTORY_TEXT,
                    icon: 'history',
                },
            ],
        });
    }

    return groups;
}

export function formatEntityType(entity_type: string): string {
    switch (entity_type) {
        case 'recarea':
            return 'Recreation Area';
        case 'campground':
            return 'Campground';
        case 'tour':
            return 'Tour';
        case 'timedentry':
            return 'Timed Entry';
        case 'timedentry_tour':
            return 'Timed Entry Tour';
        case 'ticketfacility':
            return 'Ticket Facility';
        case 'vehiclepermit':
            return 'Vehicle Permit';
        case 'activitypass':
            return 'Activity Pass';
        case 'treepermit':
            return 'Tree Permit';
        case 'interagencypass':
            return 'Interagency Pass';
        case 'venuereservations':
            return 'Venue Reservations';
        case 'venuereservations_venue':
            return 'Venue Reservations Venue';
        case 'kb':
            return 'Knowledge Base';
        case 'cms':
            return 'Content Management System';
        case 'page':
            return 'Page';
        default:
            return '';
    }
}

// cSpell:ignore timedentry, recarea, vehiclepermit, activitypass, ticketfacility, treepermit, interagencypass, venuereservations
